import { Alert, App, Form, Input, Modal, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { OperationResultAlert } from '@/shared/ui/OperationResultAlert'
import { useOperationRunner } from '@/shared/ui/useOperationRunner'
import { NAS_SECRET_MIN_LENGTH, setNasSecret, type NasEntry } from './nasApi'
import { outcomeWarning } from './outcomeWarning'
import { submitOnValid } from '@/shared/lib/submitOnValid'

interface NasSecretModalProps {
  open: boolean
  nas: NasEntry | null
  onClose: () => void
}

interface FormValues {
  secret: string
  repeat: string
}

/**
 * RADIUS umumiy sirini ALMASHTIRISH.
 *
 * ⚠️ "Ko'rsatish" emas, ALMASHTIRISH — panelda sirni o'qish yo'li umuman
 * yo'q (`NasEntry` da bunday maydon yo'q, `nasApi.ts` izohi). Bu abonent
 * paroli bilan bir xil qaror (CLAUDE.md §7): o'qish imkoniyati bo'lmasa,
 * u sizib chiqmaydi ham.
 *
 * ⚠️ OQIBAT OLDINDAN AYTILADI: yangi sir bazaga yozilgach, NAS'ning O'ZIDA
 * ham o'zgartirilmaguncha o'sha qurilmadan keladigan HAR BIR autentifikatsiya
 * so'rovi rad etiladi. Bu CoA emas — bu yerda "faol sessiyaga qo'llandi"
 * degan tushuncha yo'q, faqat baza. Shuning uchun ogohlantirish tugmadan
 * OLDIN turadi, natijadan keyin emas.
 *
 * TAKRORLASH MAYDONI bor: sir hech qachon ko'rsatilmagani uchun terishdagi
 * xatoni keyin topib bo'lmaydi — faqat qurilma ulanmay qolganda bilinardi.
 */
export function NasSecretModal({ open, nas, onClose }: NasSecretModalProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [form] = Form.useForm<FormValues>()
  const runner = useOperationRunner([queryKeys.nas.all])

  const close = () => {
    runner.clear()
    form.resetFields()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={t('nas.secretTitle')}
      onCancel={close}
      onOk={() =>
        submitOnValid(form, async (values) => {
          /*
            ⚠️ ILGARI BU YERDA `nas?.id ?? 0` TURARDI.

            Ya'ni NAS noma'lum bo'lsa, so'rov `id = 0` bilan KETARDI —
            RADIUS sirini "nol" identifikatorli yozuvga qo'yish urinishi.
            Bu tugmada `disabled` qorovuli ham yo'q edi (o'chirish
            modalidan farqli), ya'ni himoya faqat "bunday bo'lmaydi"
            degan umidga tayanardi.

            "Noma'lum" ni nolga aylantirish — bugun topilgan naqshning
            o'zi, faqat bu safar oqibati XAVFSIZLIKKA tegadi. Endi
            noma'lum bo'lsa hech narsa yuborilmaydi.
          */
          if (!nas) return
          const outcome = await runner.run(() => setNasSecret(nas.id, values.secret.trim()))
          if (outcome.status !== 'FAILED') {
            if (outcome.status === 'OK') void message.success(t('common.toastSaved'))
            else void message.warning(outcomeWarning(outcome, t))
            form.resetFields()
          }
        })
      }
      confirmLoading={runner.running}
      okText={t('nas.secretSubmit')}
      okButtonProps={{ danger: true }}
      cancelText={t('common.close')}
      destroyOnHidden
      width={520}
    >
      <Typography.Paragraph type="secondary">
        {t('nas.secretSubject', { shortname: nas?.shortname ?? '', nasname: nas?.nasname ?? '' })}
      </Typography.Paragraph>

      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('nas.secretWarnTitle')}
        description={t('nas.secretWarnText')}
      />

      {runner.result ? (
        <div style={{ marginBottom: 16 }}>
          <OperationResultAlert partialNote={false} result={runner.result} />
        </div>
      ) : null}

      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={runner.running}>
        <Form.Item
          name="secret"
          label={t('nas.secretNew')}
          extra={t('nas.secretHint', { min: NAS_SECRET_MIN_LENGTH })}
          rules={[
            { required: true, message: t('nas.validation.secretRequired') },
            {
              min: NAS_SECRET_MIN_LENGTH,
              message: t('nas.validation.secretLength', { min: NAS_SECRET_MIN_LENGTH }),
            },
          ]}
        >
          <Input.Password visibilityToggle={false} autoFocus autoComplete="new-password" />
        </Form.Item>

        <Form.Item
          name="repeat"
          label={t('nas.secretRepeat')}
          dependencies={['secret']}
          rules={[
            { required: true, message: t('nas.validation.secretRepeatRequired') },
            ({ getFieldValue }) => ({
              validator: (_, value: string | undefined) =>
                !value || value === (getFieldValue('secret') as string)
                  ? Promise.resolve()
                  : Promise.reject(new Error(t('nas.validation.secretMismatch'))),
            }),
          ]}
        >
          <Input.Password visibilityToggle={false} autoComplete="new-password" />
        </Form.Item>
      </Form>
    </Modal>
  )
}
